import React, { useRef, useState, useEffect, useCallback, useLayoutEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import SignaturePad from "react-signature-canvas";
import "./SignOffTab.css";

const SIGNATURE_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
const SIGNATURE_RENDER_MAX_WIDTH = 600;
const SIGNATURE_RENDER_MAX_HEIGHT = 160;

/** Normalize uploaded images to PNG data URLs — same shape as SignaturePad `.toDataURL("image/png")`. */
function isAllowedSignatureImageFile(file) {
  const t = String(file?.type || "").toLowerCase();
  if (/^image\/(png|jpeg|webp)$/i.test(t)) return true;
  const name = String(file?.name || "").toLowerCase();
  return /\.(png|jpe?g|webp)$/.test(name);
}

async function fileToSignaturePngDataUrl(file) {
  if (!isAllowedSignatureImageFile(file)) {
    throw new Error("Please use a PNG, JPG, or WebP image.");
  }
  if (file.size > SIGNATURE_UPLOAD_MAX_BYTES) {
    throw new Error("Image must be 2 MB or smaller.");
  }
  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not read that image."));
      image.src = blobUrl;
    });
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    if (!(width > 0 && height > 0)) {
      throw new Error("Invalid image dimensions.");
    }
    const scale = Math.min(
      SIGNATURE_RENDER_MAX_WIDTH / width,
      SIGNATURE_RENDER_MAX_HEIGHT / height,
      1
    );
    const outW = Math.max(1, Math.floor(width * scale));
    const outH = Math.max(1, Math.floor(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    const dataUrl = canvas.toDataURL("image/png");
    if (!dataUrl || dataUrl.length < 200) {
      throw new Error("Could not convert image to signature format.");
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

const SIGNATURE_PAD_MIN_WIDTH = 200;

const formatSignatureDate = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB");
  }
  return String(raw);
};

const SignOffTab = ({
  formState,
  updateFormField,
  onSave,
  onCancel,
  onSendForRTM,
  canApprove,
  canReject,
  onApprove,
  onReject,
  onRtmApprove,
  onRtmDisapprove,
  isReadOnly = false,
  currentUserRole = "",
  employeeSignMode = false,
}) => {
  const sigPadRef = useRef(null);
  const signatureCanvasShellRef = useRef(null);
  const fileInputRef = useRef(null);
  /** True after a successful upload until drawing overwrites — avoids synchronous pad `onEnd` clearing before `uploadPreviewUrl` state commits. */
  const uploadActiveRef = useRef(false);
  const [signatureImage, setSignatureImage] = useState(formState?.signatureImage || "");
  const [signaturePadWidth, setSignaturePadWidth] = useState(SIGNATURE_RENDER_MAX_WIDTH);
  const [uploadError, setUploadError] = useState("");
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  /** Data URL shown in upload panel after a successful file choose; cleared when drawing saves or user removes. */
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const isSigned = employeeSignMode
    ? formState?.employeeSigned === true ||
      formState?.employeeSigned === 1 ||
      formState?.employeeSigned === "1"
    : formState?.signOff === 1 || formState?.signOff === true || formState?.signOff === "1";
  const employeeStatusDisplay = String(
    formState?.employeeConfirmationStatusDisplay || "Pending"
  ).trim();
  const employeeStatusIsSigned =
    employeeSignMode &&
    (isSigned ||
      /signed|approved|confirmed/i.test(employeeStatusDisplay));
  const rawFlagValue = formState?.show_rtm_and_update_button;
  const flag = String(rawFlagValue) === "1" ? 1 : 0;

  useLayoutEffect(() => {
    if (isSigned) return undefined;
    const el = signatureCanvasShellRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const syncWidth = () => {
      const w = el.clientWidth;
      if (w < 40) return;
      const next = Math.min(
        SIGNATURE_RENDER_MAX_WIDTH,
        Math.max(SIGNATURE_PAD_MIN_WIDTH, Math.floor(w))
      );
      setSignaturePadWidth((prev) => (prev === next ? prev : next));
    };

    syncWidth();
    const ro = new ResizeObserver(() => {
      syncWidth();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isSigned]);

  if (process.env.NODE_ENV === "development") {
    console.log("SignOffTab show_rtm_and_update_button:", {
      rawFlagValue,
      flag,
    });
  }

  const showRTMandUpdateButton = flag === 1;
  const normalizedRole = String(currentUserRole).toLowerCase();
  const isSupervisorReadOnly = isReadOnly && normalizedRole === "supervisor";
  const isHrReadOnly = isReadOnly && normalizedRole === "hr";
  const isOneLevelWorkflow = Number(formState?.approval_levels ?? formState?.approvalLevels ?? 0) === 1;
  const hideApproveRejectActions = isOneLevelWorkflow
    ? false
    : normalizedRole === "supervisor" || normalizedRole === "hr";
  const employeeSignature = employeeSignMode
    ? ""
    : String(formState?.employeeSignature || "").trim();
  const employeeSignedDate = formatSignatureDate(formState?.employeeSignedAt);
  const appraiserSignedDate = formatSignatureDate(formState?.signOffDateTime);

  // Update signature image when formState changes
  useEffect(() => {
    if (formState?.signatureImage) {
      setSignatureImage(formState.signatureImage);
    }
  }, [formState?.signatureImage]);

  const applySignatureFromDataUrl = useCallback(
    (dataUrl) => {
      setUploadError("");
      setSignatureImage(dataUrl);
      updateFormField("signatureImage", dataUrl);
    },
    [updateFormField]
  );

  const consumeUploadFile = useCallback(
    async (file) => {
      if (!file || isSigned || isReadOnly) return;
      setUploadError("");
      try {
        const dataUrl = await fileToSignaturePngDataUrl(file);
        applySignatureFromDataUrl(dataUrl);
        uploadActiveRef.current = true;
        setUploadPreviewUrl(dataUrl);
        if (sigPadRef.current && typeof sigPadRef.current.clear === "function") {
          sigPadRef.current.clear();
        }
      } catch (e) {
        setUploadError(e?.message || "Upload failed.");
      }
    },
    [applySignatureFromDataUrl, isSigned, isReadOnly]
  );

  const handleUploadInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) consumeUploadFile(f);
    e.target.value = "";
  };

  const preventDragDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const clearSignature = () => {
    if (!isSigned && !isReadOnly) {
      sigPadRef.current?.clear?.();
      setSignatureImage("");
      setUploadPreviewUrl(null);
      uploadActiveRef.current = false;
      setUploadError("");
      updateFormField("signatureImage", "");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeUploadedPreview = () => {
    if (isSigned || isReadOnly) return;
    uploadActiveRef.current = false;
    setUploadPreviewUrl(null);
    setSignatureImage("");
    setUploadError("");
    updateFormField("signatureImage", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const saveSignature = () => {
    try {
      if (!sigPadRef.current) {
        console.warn("Signature pad ref is not available");
        return;
      }

      // Check if signature pad is empty - call isEmpty() method
      const isEmpty = typeof sigPadRef.current.isEmpty === 'function' 
        ? sigPadRef.current.isEmpty() 
        : true;
      
      if (isEmpty) {
        // Keep uploaded signature — empty pad fires `onEnd` right after programmatic clear().
        if (uploadActiveRef.current || uploadPreviewUrl) return;
        setSignatureImage("");
        updateFormField("signatureImage", "");
        setUploadPreviewUrl(null);
        return;
      }

      uploadActiveRef.current = false;
      setUploadPreviewUrl(null);
      
      // Try different methods to get the canvas
      let canvas = null;
      
      // Method 1: Try getTrimmedCanvas (standard method)
      if (typeof sigPadRef.current.getTrimmedCanvas === 'function') {
        try {
          canvas = sigPadRef.current.getTrimmedCanvas();
        } catch (e) {
          console.warn("getTrimmedCanvas failed, trying alternatives:", e);
        }
      }
      
      // Method 2: Try getCanvas (alternative method)
      if (!canvas && typeof sigPadRef.current.getCanvas === 'function') {
        try {
          canvas = sigPadRef.current.getCanvas();
        } catch (e) {
          console.warn("getCanvas failed:", e);
        }
      }
      
      // Method 3: Access canvas directly
      if (!canvas && sigPadRef.current.canvas) {
        canvas = sigPadRef.current.canvas;
      }
      
      // Convert canvas to image
      if (canvas && typeof canvas.toDataURL === 'function') {
        const image = canvas.toDataURL("image/png");
        setUploadError("");
        setSignatureImage(image);
        updateFormField("signatureImage", image);
      } else {
        console.error("Canvas not available or toDataURL method not found");
        // If signature exists but we can't get canvas, still proceed with save
        // The signature might be saved separately
      }
    } catch (error) {
      console.error("Error saving signature:", error);
      // Don't block the save operation if signature save fails
      // The form can still be saved without the signature
    }
  };

  const resolveFinalSignatureImage = () => {
    let finalSignatureImage = signatureImage;

    // First, try to get signature from the pad (always check canvas directly)
    if (sigPadRef.current) {
      try {
        let canvas = null;
        
        // Try to get the regular canvas first (more reliable than trimmed)
        if (sigPadRef.current.canvas) {
          canvas = sigPadRef.current.canvas;
        } else if (typeof sigPadRef.current.getCanvas === 'function') {
          try {
            canvas = sigPadRef.current.getCanvas();
          } catch (e) {
            console.warn("getCanvas failed:", e);
          }
        } else if (typeof sigPadRef.current.getTrimmedCanvas === 'function') {
          try {
            canvas = sigPadRef.current.getTrimmedCanvas();
          } catch (e) {
            console.warn("getTrimmedCanvas failed:", e);
          }
        }

        // If we have a canvas, convert it to image
        if (canvas && typeof canvas.toDataURL === 'function') {
          const canvasImage = canvas.toDataURL("image/png");
          
          // Check if isEmpty method exists
          let isEmpty = true;
          if (typeof sigPadRef.current.isEmpty === 'function') {
            try {
              isEmpty = sigPadRef.current.isEmpty();
            } catch (e) {
              console.warn("isEmpty() failed:", e);
              // If isEmpty fails, check image length as fallback
              isEmpty = !canvasImage || canvasImage.length < 200;
            }
          } else {
            // If isEmpty doesn't exist, use image length as indicator
            isEmpty = !canvasImage || canvasImage.length < 200;
          }
          
          // Use canvas image if pad is not empty
          if (!isEmpty && canvasImage) {
            finalSignatureImage = canvasImage;
          }
        }
      } catch (error) {
        console.error("Error checking signature pad:", error);
      }
    }

    // Use the best available signature image (prefer newly captured, fallback to existing)
    if (!finalSignatureImage && signatureImage) {
      finalSignatureImage = signatureImage;
    }

    // Final check - if we still don't have a signature, show alert
    if (!finalSignatureImage || finalSignatureImage.trim() === '' || finalSignatureImage === 'data:image/png;base64,') {
      console.log("Signature check failed:", {
        finalSignatureImage: finalSignatureImage ? `exists (length: ${finalSignatureImage.length})` : 'missing',
        signatureImage: signatureImage ? `exists (length: ${signatureImage.length})` : 'missing',
        sigPadRefExists: !!sigPadRef.current,
        isEmpty: sigPadRef.current && typeof sigPadRef.current.isEmpty === 'function' ? sigPadRef.current.isEmpty() : 'N/A'
      });
      alert("Please draw your signature or upload an image before signing off.");
      return null;
    }

    setSignatureImage(finalSignatureImage);
    updateFormField("signatureImage", finalSignatureImage);
    return finalSignatureImage;
  };

  const handleSignOff = async () => {
    const finalSignatureImage = resolveFinalSignatureImage();
    if (!finalSignatureImage) return;
    updateFormField("signOff", 1);
    updateFormField("signatureImage", finalSignatureImage);
    setSignatureImage(finalSignatureImage);
    if (onSave) {
      await Promise.resolve(
        onSave({
          redirect: false,
          fields: { signOff: 1, signatureImage: finalSignatureImage },
        })
      );
    }
  };

  const handleEmployeeApprove = () => {
    try {
      saveSignature();
    } catch (error) {
      console.error("Error saving signature:", error);
    }
    const finalSignatureImage = resolveFinalSignatureImage();
    if (!finalSignatureImage) return;
    if (onApprove) onApprove(finalSignatureImage);
  };

  const handleUpdateAndSendForRTM = async () => {
    try {
      saveSignature();
    } catch (error) {
      console.error("Error saving signature:", error);
    }

    const finalSignatureImage = resolveFinalSignatureImage();
    if (finalSignatureImage) {
      updateFormField("signOff", 1);
      updateFormField("signatureImage", finalSignatureImage);
      setSignatureImage(finalSignatureImage);
    }

    try {
      if (onSendForRTM) {
        await Promise.resolve(onSendForRTM());
        return;
      }
      if (onSave) {
        await Promise.resolve(
          onSave({
            redirect: false,
            fields: {
              signOff: 1,
              signatureImage: finalSignatureImage || formState?.signatureImage,
            },
          })
        );
      }
    } catch (error) {
      console.error("Error updating and sending for RTM:", error);
    }
  };

  return (
    <>
      <div>
        <Row>
          {(employeeSignMode || isSigned) && (
            <Col lg={4}>
              <Form.Group className="mb-3">
                <Form.Label className="label-input">Status</Form.Label>
                <div
                  className="form-control input-field w-100"
                  style={{ padding: "6px 12px", backgroundColor: "#f8f9fa" }}
                >
                  {employeeSignMode ? (
                    <span
                      style={{
                        color: employeeStatusIsSigned ? "#28a745" : "#6c757d",
                        fontWeight: "bold",
                      }}
                    >
                      {employeeStatusIsSigned ? "✓ " : ""}
                      {employeeStatusDisplay}
                    </span>
                  ) : (
                    <span style={{ color: "#28a745", fontWeight: "bold" }}>✓ Signed</span>
                  )}
                </div>
              </Form.Group>
            </Col>
          )}
        </Row>
        {isSigned ? (
          <Row>
            <Col xs={12} lg={employeeSignature ? 6 : 12} className="mb-3 mb-lg-0">
              <div>
                <h6 className="mb-2 label-input">
                  {employeeSignMode ? "Employee Signature" : "Evaluator Signature"}
                </h6>
                {signatureImage ? (
                  <div
                    className="border rounded-3 p-3 d-inline-block"
                    style={{ backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                  >
                    <img
                      src={signatureImage}
                      alt="Evaluator Signature"
                      style={{ maxWidth: "100%", width: "auto", maxHeight: "180px", display: "block" }}
                    />
                  </div>
                ) : (
                  <div className="text-muted">No signature image available</div>
                )}
                {!employeeSignMode && appraiserSignedDate ? (
                  <div className="small text-muted mt-2">Signed on {appraiserSignedDate}</div>
                ) : null}
              </div>
            </Col>
            {employeeSignature ? (
              <Col xs={12} lg={6}>
                <div>
                  <h6 className="mb-2 label-input">Employee Signature</h6>
                  <div
                    className="border rounded-3 p-3 d-inline-block"
                    style={{ backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                  >
                    <img
                      src={employeeSignature}
                      alt="Employee signature"
                      style={{ maxWidth: "100%", width: "auto", maxHeight: "180px", display: "block" }}
                    />
                  </div>
                  {employeeSignedDate ? (
                    <div className="small text-muted mt-2">Signed on {employeeSignedDate}</div>
                  ) : null}
                </div>
              </Col>
            ) : null}
          </Row>
        ) : (
          <div className="signature-module-card p-3 p-md-4">
            <h6 className="mb-2 fw-semibold text-dark">
              {employeeSignMode ? "Employee Signature" : "Evaluator Signature"}
            </h6>
            <p className="text-muted mb-4 small" style={{ maxWidth: "52rem", lineHeight: 1.5 }}>
              Use the drawing pad or upload a file. Accepted formats: PNG, JPG, or WebP (max{" "}
              {Math.round(SIGNATURE_UPLOAD_MAX_BYTES / (1024 * 1024))} MB). Uploads are normalized to PNG
              to match drawings.
            </p>

            <div
              className="d-flex flex-column flex-lg-row align-items-lg-stretch"
              style={{ pointerEvents: isReadOnly ? "none" : "auto" }}
            >
              <div className="flex-fill" style={{ minWidth: 0 }}>
                <div className="signature-panel-inner">
                  <div className="signature-panel-title">Draw here</div>
                  <p className="signature-panel-hint mb-3 mb-lg-2">
                    Sign with your mouse, trackpad, or touchscreen.
                  </p>
                  <div className="signature-canvas-shell" ref={signatureCanvasShellRef}>
                    <SignaturePad
                      key={signaturePadWidth}
                      ref={sigPadRef}
                      penColor="#1f2937"
                      onEnd={() => {
                        saveSignature();
                      }}
                      canvasProps={{
                        width: signaturePadWidth,
                        height: SIGNATURE_RENDER_MAX_HEIGHT,
                        className: "signature-canvas",
                      }}
                    />
                  </div>
                  <div className="signature-toolbar">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary px-3"
                      onClick={clearSignature}
                      disabled={isSigned || isReadOnly}
                    >
                      Clear canvas
                    </button>
                  </div>
                </div>
              </div>

              <div className="signature-or-horizontal px-2" aria-hidden="true">
                <hr />
                <span className="signature-or-badge--mobile">OR</span>
                <hr />
              </div>

              <div className="signature-or-vertical" aria-hidden="true">
                <span className="signature-or-badge">OR</span>
              </div>

              <div className="flex-fill" style={{ minWidth: 0 }}>
                <div className="signature-panel-inner">
                  <div className="signature-panel-title">Upload an image</div>
                  <p className="signature-panel-hint mb-3 mb-lg-2">
                    Drag and drop onto the zone below, or click to choose a file from your device. <br />
                    <small className="text-muted">Accepted formats: PNG, JPG, or WebP (max 2 MB)</small>
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="d-none"
                    disabled={isSigned || isReadOnly}
                    onChange={handleUploadInputChange}
                    aria-label="Upload signature image"
                  />
                  {uploadPreviewUrl ? (
                    <div
                      className="signature-upload-preview position-relative d-flex align-items-center justify-content-center"
                      aria-live="polite"
                    >
                      <button
                        type="button"
                        className="btn btn-sm btn-light border rounded-circle position-absolute d-flex align-items-center justify-content-center p-0 signature-upload-remove shadow-sm"
                        style={{ top: 8, right: 8, width: 32, height: 32, zIndex: 2 }}
                        onClick={removeUploadedPreview}
                        disabled={isSigned || isReadOnly}
                        aria-label="Remove uploaded image"
                        title="Remove"
                      >
                        <span className="lh-1 fs-5 text-secondary" aria-hidden>
                          ×
                        </span>
                      </button>
                      <img
                        src={uploadPreviewUrl}
                        alt="Uploaded signature preview"
                        className="signature-upload-preview-img"
                      />
                    </div>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (!isSigned && !isReadOnly) fileInputRef.current?.click();
                        }
                      }}
                      onClick={() => {
                        if (!isSigned && !isReadOnly) fileInputRef.current?.click();
                      }}
                      onDragEnter={(e) => {
                        preventDragDefaults(e);
                        if (!isReadOnly) setIsDraggingFile(true);
                      }}
                      onDragLeave={(e) => {
                        preventDragDefaults(e);
                        setIsDraggingFile(false);
                      }}
                      onDragOver={preventDragDefaults}
                      onDrop={(e) => {
                        preventDragDefaults(e);
                        setIsDraggingFile(false);
                        const f = e.dataTransfer?.files?.[0];
                        if (f) consumeUploadFile(f);
                      }}
                      className={`signature-upload-shell text-center px-4 py-4 ${
                        isDraggingFile ? "signature-upload-shell--drag border-primary" : ""
                      }`}
                      style={{
                        cursor: isReadOnly ? "default" : "pointer",
                        border: `2px dashed ${
                          isDraggingFile ? "#0d6efd" : "#94a3b8"
                        }`,
                        backgroundColor: isDraggingFile ? "#eff6ff" : "#fafafa",
                        color: "#374151",
                      }}
                    >
                      <div className="d-flex flex-column align-items-center gap-2">
                        <span
                          className="rounded-circle bg-white border d-inline-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, fontSize: 20, color: "#64748b" }}
                          aria-hidden
                        >
                          ↑
                        </span>
                        <div>
                          <div className="fw-semibold" style={{ fontSize: "0.9375rem" }}>
                            Drop Signature here
                          </div>
                          <div className="small text-muted mt-1">or click to browse files</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {uploadError ? (
                    <div className="text-danger small mt-2">{uploadError}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="mt-3">
          <button type="button" className="c-b-btn" onClick={onCancel}>Cancel</button>
          {isHrReadOnly || isSupervisorReadOnly ? null : !isSigned ? (
            employeeSignMode ? (
              <button
                type="button"
                className="n-u-btn ms-2"
                onClick={handleEmployeeApprove}
                disabled={isSigned || isReadOnly}
              >
                Sign
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="n-u-btn ms-2"
                  onClick={() => {
                    try {
                      saveSignature();
                    } catch (error) {
                      console.error("Error saving signature:", error);
                    }
                    onSave();
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="n-u-btn ms-2"
                  onClick={handleSignOff}
                  disabled={isSigned || isReadOnly}
                >
                  Sign Off
                </button>
              </>
            )
          ) : showRTMandUpdateButton ? (
            <>
              <button
                type="button"
                className="n-u-btn ms-2"
                onClick={handleUpdateAndSendForRTM}
                disabled={!formState?.id}
              >
                Update & send to RTM
              </button>
            </>
          ) : (
            <>
              {formState?.id && canApprove && !hideApproveRejectActions && (
                <button
                  type="button"
                  className="n-u-btn ms-2"
                  onClick={() => onApprove && onApprove()}
                >
                  Approve
                </button>
              )}
              {formState?.id && canReject && !hideApproveRejectActions && (
                <button
                  type="button"
                  className="delete-btn ms-2"
                  onClick={() => onReject && onReject()}
                >
                  Reject
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SignOffTab;
